import { useState, useCallback } from "react";
import axios from "axios";
import { Calendar, CheckCircle2, ChevronRight, ChevronLeft, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPECIAL_NEEDS_KEYS = [
  "needTrach", "needVent", "needFeedingTube", "needMedication",
  "needMobility", "needHygiene", "needWoundCare", "needMonitoring"
];

const INITIAL_FORM = {
  client_name: "", email: "", phone: "", service_type: "",
  preferred_date: "", preferred_time: "", notes: "",
  patient_name: "", patient_age: "", patient_type: "",
  patient_condition: "", medical_history: "", special_needs: [],
};

const FALLBACK_SLOTS = [
  "08:00 AM", "09:00 AM", "10:00 AM", "11:00 AM",
  "12:00 PM", "01:00 PM", "02:00 PM", "03:00 PM",
  "04:00 PM", "05:00 PM", "06:00 PM"
];

const getMinDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().split('T')[0];
};

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-[#1A2332]">{label}</Label>
    {children}
  </div>
);

const SelectField = ({ label, testId, value, onChange, placeholder, options, disabled }) => (
  <Field label={label}>
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger data-testid={testId} className="border-[#003A4B]/20">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </Field>
);

const SuccessCard = ({ t }) => (
  <Card className="border-0 shadow-xl bg-gradient-to-br from-[#003A4B] to-[#1B5E6B] text-white p-12 text-center">
    <CheckCircle2 className="w-16 h-16 mx-auto mb-4" />
    <h3 className="text-2xl font-bold mb-2">{t.booking.success}</h3>
    <p className="text-white/80">{t.booking.successMsg}</p>
  </Card>
);

const getStep2Style = (step, canGoToStep2) => {
  if (step === 2) return "bg-[#003A4B] text-white shadow-lg";
  if (canGoToStep2) return "bg-[#F3F0E7] text-[#4A5568] hover:bg-[#003A4B]/10 cursor-pointer";
  return "bg-gray-100 text-gray-400 cursor-not-allowed";
};

const StepIndicator = ({ step, setStep, canGoToStep2, t }) => (
  <div className="flex items-center justify-center gap-4 mb-8">
    <button
      onClick={() => setStep(1)}
      data-testid="step-1-btn"
      className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
        step === 1
          ? "bg-[#003A4B] text-white shadow-lg"
          : "bg-[#F3F0E7] text-[#4A5568] hover:bg-[#003A4B]/10"
      }`}
    >
      <Calendar className="w-4 h-4" />
      {t.booking.stepSchedule}
    </button>
    <div className="w-8 h-0.5 bg-[#C89D4B]/30"></div>
    <button
      onClick={() => canGoToStep2 && setStep(2)}
      data-testid="step-2-btn"
      className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${getStep2Style(step, canGoToStep2)}`}
    >
      <ClipboardList className="w-4 h-4" />
      {t.booking.stepPatient}
    </button>
  </div>
);

const ContactInfoFields = ({ formData, setFormData, serviceOptions, t }) => (
  <>
    <div className="grid md:grid-cols-2 gap-6">
      <Field label={t.booking.fullName}>
        <Input
          data-testid="booking-name"
          value={formData.client_name}
          onChange={(e) => setFormData(f => ({ ...f, client_name: e.target.value }))}
          required
          className="border-[#003A4B]/20 focus:ring-[#003A4B]"
        />
      </Field>
      <Field label={t.booking.email}>
        <Input
          type="email"
          data-testid="booking-email"
          value={formData.email}
          onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))}
          required
          className="border-[#003A4B]/20 focus:ring-[#003A4B]"
        />
      </Field>
    </div>
    <div className="grid md:grid-cols-2 gap-6">
      <Field label={t.booking.phone}>
        <Input
          type="tel"
          data-testid="booking-phone"
          value={formData.phone}
          onChange={(e) => setFormData(f => ({ ...f, phone: e.target.value }))}
          required
          className="border-[#003A4B]/20 focus:ring-[#003A4B]"
        />
      </Field>
      <SelectField
        label={t.booking.serviceType}
        testId="booking-service"
        value={formData.service_type}
        onChange={(v) => setFormData(f => ({ ...f, service_type: v }))}
        placeholder={t.booking.selectService}
        options={serviceOptions}
      />
    </div>
  </>
);

const SchedulingFields = ({ formData, setFormData, availableSlots, handleDateChange, t }) => (
  <>
    <div className="grid md:grid-cols-2 gap-6">
      <Field label={t.booking.preferredDate}>
        <Input
          type="date"
          data-testid="booking-date"
          value={formData.preferred_date}
          onChange={(e) => handleDateChange(e.target.value)}
          min={getMinDate()}
          required
          className="border-[#003A4B]/20 focus:ring-[#003A4B]"
        />
      </Field>
      <SelectField
        label={t.booking.preferredTime}
        testId="booking-time"
        value={formData.preferred_time}
        onChange={(v) => setFormData(f => ({ ...f, preferred_time: v }))}
        placeholder={t.booking.selectTime}
        options={availableSlots.map(slot => ({ value: slot, label: slot }))}
        disabled={!formData.preferred_date}
      />
    </div>
    <Field label={t.booking.notes}>
      <Textarea
        data-testid="booking-notes"
        value={formData.notes}
        onChange={(e) => setFormData(f => ({ ...f, notes: e.target.value }))}
        placeholder={t.booking.notesPlaceholder}
        rows={2}
        className="border-[#003A4B]/20 focus:ring-[#003A4B]"
      />
    </Field>
  </>
);

const ScheduleStep = ({ formData, setFormData, availableSlots, handleDateChange, serviceOptions, canGoToStep2, setStep, t }) => (
  <div className="space-y-6" data-testid="booking-step-1">
    <ContactInfoFields formData={formData} setFormData={setFormData} serviceOptions={serviceOptions} t={t} />
    <SchedulingFields formData={formData} setFormData={setFormData} availableSlots={availableSlots} handleDateChange={handleDateChange} t={t} />
    <Button
      type="button"
      data-testid="booking-next-step"
      onClick={() => canGoToStep2 && setStep(2)}
      disabled={!canGoToStep2}
      className="w-full bg-[#003A4B] hover:bg-[#002A38] text-white rounded-full py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
    >
      {t.booking.stepPatient}
      <ChevronRight className="w-5 h-5 ml-2" />
    </Button>
  </div>
);

const PatientInfoBanner = ({ t }) => (
  <div className="bg-gradient-to-r from-[#F3F0E7] to-[#F8F5EE] rounded-2xl p-5 border-l-4 border-[#C89D4B]">
    <h3 className="font-semibold text-[#1A2332] flex items-center gap-2 mb-1">
      <ClipboardList className="w-5 h-5 text-[#C89D4B]" />
      {t.booking.patientInfoTitle}
    </h3>
    <p className="text-sm text-[#718096]">{t.booking.patientInfoDesc}</p>
  </div>
);

const PatientDetailFields = ({ formData, setFormData, patientTypeOptions, t }) => (
  <>
    <div className="grid md:grid-cols-3 gap-6">
      <Field label={t.booking.patientName}>
        <Input
          data-testid="patient-name"
          value={formData.patient_name}
          onChange={(e) => setFormData(f => ({ ...f, patient_name: e.target.value }))}
          className="border-[#003A4B]/20 focus:ring-[#003A4B]"
        />
      </Field>
      <Field label={t.booking.patientAge}>
        <Input
          data-testid="patient-age"
          value={formData.patient_age}
          onChange={(e) => setFormData(f => ({ ...f, patient_age: e.target.value }))}
          placeholder="E.g. 75"
          className="border-[#003A4B]/20 focus:ring-[#003A4B]"
        />
      </Field>
      <SelectField
        label={t.booking.patientType}
        testId="patient-type"
        value={formData.patient_type}
        onChange={(v) => setFormData(f => ({ ...f, patient_type: v }))}
        placeholder={t.booking.selectPatientType}
        options={patientTypeOptions}
      />
    </div>
    <Field label={t.booking.patientCondition}>
      <Input
        data-testid="patient-condition"
        value={formData.patient_condition}
        onChange={(e) => setFormData(f => ({ ...f, patient_condition: e.target.value }))}
        placeholder={t.booking.patientConditionPlaceholder}
        className="border-[#003A4B]/20 focus:ring-[#003A4B]"
      />
    </Field>
    <Field label={t.booking.medicalHistory}>
      <Textarea
        data-testid="patient-medical-history"
        value={formData.medical_history}
        onChange={(e) => setFormData(f => ({ ...f, medical_history: e.target.value }))}
        placeholder={t.booking.medicalHistoryPlaceholder}
        rows={3}
        className="border-[#003A4B]/20 focus:ring-[#003A4B]"
      />
    </Field>
  </>
);

const SpecialNeedsPicker = ({ specialNeeds, toggleNeed, t }) => (
  <div className="space-y-3">
    <Label className="text-[#1A2332]">{t.booking.specialNeeds}</Label>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {SPECIAL_NEEDS_KEYS.map((key) => {
        const isSelected = specialNeeds.includes(key);
        return (
          <button
            key={key}
            type="button"
            data-testid={`need-${key}`}
            onClick={() => toggleNeed(key)}
            className={`p-3 rounded-xl text-sm font-medium border transition-all text-left ${
              isSelected
                ? "bg-[#003A4B] text-white border-[#003A4B] shadow-md"
                : "bg-white text-[#4A5568] border-[#003A4B]/15 hover:border-[#C89D4B]/50"
            }`}
          >
            {t.booking[key]}
          </button>
        );
      })}
    </div>
  </div>
);

const PatientStepButtons = ({ setStep, loading, t }) => (
  <div className="flex gap-4">
    <Button
      type="button"
      variant="outline"
      data-testid="booking-prev-step"
      onClick={() => setStep(1)}
      className="flex-1 rounded-full py-6 text-lg border-2 border-[#003A4B]/20 text-[#4A5568] hover:bg-[#F3F0E7]"
    >
      <ChevronLeft className="w-5 h-5 mr-2" />
      {t.booking.stepSchedule}
    </Button>
    <Button
      type="submit"
      data-testid="booking-submit"
      disabled={loading}
      className="flex-[2] bg-[#003A4B] hover:bg-[#002A38] text-white rounded-full py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
    >
      <Calendar className="w-5 h-5 mr-2" />
      {loading ? "..." : t.booking.submit}
    </Button>
  </div>
);

const PatientStep = ({ formData, setFormData, toggleNeed, patientTypeOptions, setStep, loading, t }) => (
  <div className="space-y-6" data-testid="booking-step-2">
    <PatientInfoBanner t={t} />
    <PatientDetailFields formData={formData} setFormData={setFormData} patientTypeOptions={patientTypeOptions} t={t} />
    <SpecialNeedsPicker specialNeeds={formData.special_needs} toggleNeed={toggleNeed} t={t} />
    <PatientStepButtons setStep={setStep} loading={loading} t={t} />
  </div>
);

const useBookingForm = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleDateChange = useCallback((date) => {
    setFormData(f => ({ ...f, preferred_date: date, preferred_time: "" }));
    if (date) {
      axios.get(`${API}/available-slots?date=${date}`)
        .then(r => setAvailableSlots(r.data.available_slots))
        .catch(() => setAvailableSlots(FALLBACK_SLOTS));
    }
  }, []);

  const toggleNeed = useCallback((need) => {
    setFormData(prev => ({
      ...prev,
      special_needs: prev.special_needs.includes(need)
        ? prev.special_needs.filter(n => n !== need)
        : [...prev.special_needs, need]
    }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/appointments`, formData);
      setSubmitted(true);
      setFormData(INITIAL_FORM);
      setStep(1);
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to book appointment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const canGoToStep2 = Boolean(
    formData.client_name && formData.email && formData.phone
    && formData.service_type && formData.preferred_date && formData.preferred_time
  );

  return { step, setStep, formData, setFormData, availableSlots, loading, submitted, error, handleDateChange, toggleNeed, handleSubmit, canGoToStep2 };
};

const BookingHeader = ({ t }) => (
  <div className="text-center max-w-2xl mx-auto mb-12">
    <span className="text-[#C89D4B] font-semibold uppercase tracking-widest text-sm">{t.booking.title}</span>
    <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1A2332] mt-3">
      {t.booking.subtitle}
    </h2>
    <p className="text-[#718096] mt-4">{t.booking.description}</p>
  </div>
);

export const BookingSection = () => {
  const { t } = useLanguage();
  const form = useBookingForm();

  const serviceOptions = [
    { value: "basic", label: t.calculator.basicCare },
    { value: "personal", label: t.calculator.personalCare },
    { value: "specialized", label: t.calculator.specializedCare },
  ];

  const patientTypeOptions = [
    { value: "elderly", label: t.booking.patientTypeElderly },
    { value: "child", label: t.booking.patientTypeChild },
    { value: "ventilator", label: t.booking.patientTypeVentilator },
    { value: "palliative", label: t.booking.patientTypePalliative },
    { value: "post-surgery", label: t.booking.patientTypePostSurgery },
  ];

  return (
    <section id="booking" data-testid="booking-section" className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        <BookingHeader t={t} />
        {form.submitted ? (
          <SuccessCard t={t} />
        ) : (
          <>
            {form.error && <div data-testid="booking-error" className="bg-red-50 text-red-600 p-4 rounded-xl text-sm mb-4">{form.error}</div>}
            <StepIndicator step={form.step} setStep={form.setStep} canGoToStep2={form.canGoToStep2} t={t} />
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8">
                <form onSubmit={form.handleSubmit}>
                  {form.step === 1 && (
                    <ScheduleStep
                      formData={form.formData} setFormData={form.setFormData}
                      availableSlots={form.availableSlots} handleDateChange={form.handleDateChange}
                      serviceOptions={serviceOptions} canGoToStep2={form.canGoToStep2}
                      setStep={form.setStep} t={t}
                    />
                  )}
                  {form.step === 2 && (
                    <PatientStep
                      formData={form.formData} setFormData={form.setFormData}
                      toggleNeed={form.toggleNeed} patientTypeOptions={patientTypeOptions}
                      setStep={form.setStep} loading={form.loading} t={t}
                    />
                  )}
                </form>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </section>
  );
};
